import { useState } from 'react';
import { api, ApiError, formatDateTime, scr } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { VoluntaryStatement } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, Modal, PageHeader, Spinner, StatCard, StatusBadge } from '../../ui/components';

export function MyVoluntarySavings() {
  const { persona } = useAuth();
  const statementQ = useApiQuery<VoluntaryStatement>('/api/v1/voluntary/me/statement');
  const [modal, setModal] = useState<'deposit' | 'withdraw' | 'standing' | null>(null);
  const notFound = statementQ.error?.toLowerCase().includes('not found') ?? false;

  const reload = () => {
    setModal(null);
    statementQ.reload();
  };

  return (
    <div>
      <PageHeader
        title="My Voluntary Savings"
        subtitle="Third-pillar top-up savings inside your SPF membership"
        actions={
          <>
            <button className="btn-secondary" onClick={() => setModal('standing')}>
              Standing order
            </button>
            <button className="btn-secondary" onClick={() => setModal('withdraw')}>
              Request withdrawal
            </button>
            <button className="btn-primary" onClick={() => setModal('deposit')}>
              + Make a deposit
            </button>
          </>
        }
      />

      {statementQ.loading ? (
        <Spinner />
      ) : notFound ? (
        <EmptyState title="No voluntary savings account yet" hint="Make your first deposit to open one." />
      ) : statementQ.error ? (
        <ErrorNote message={statementQ.error} />
      ) : statementQ.data ? (
        <>
          <div className="mb-6 grid gap-3 sm:grid-cols-2">
            <StatCard label="Current balance" value={scr(statementQ.data.balance)} tone="good" />
            <StatCard
              label="Active standing orders"
              value={statementQ.data.standingOrders.filter((s) => s.active).length}
            />
          </div>

          <div className="card mb-6 overflow-x-auto">
            <h2 className="border-b border-slate-100 px-4 py-3 text-sm font-bold text-slate-700">Transaction history</h2>
            {statementQ.data.transactions.length === 0 ? (
              <EmptyState title="No transactions yet" />
            ) : (
              <table className="w-full">
                <thead>
                  <tr>
                    <th className="table-head">Date</th>
                    <th className="table-head">Type</th>
                    <th className="table-head">Reference</th>
                    <th className="table-head text-right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {[...statementQ.data.transactions].reverse().map((t, i) => (
                    <tr key={i} className="hover:bg-slate-50">
                      <td className="table-cell text-slate-500">{formatDateTime(t.at)}</td>
                      <td className="table-cell">{t.type}</td>
                      <td className="table-cell text-xs text-slate-500">{t.ref}</td>
                      <td className={`table-cell text-right tabular-nums ${t.amount < 0 ? 'text-rose-600' : 'text-seagreen-600'}`}>
                        {scr(t.amount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className="card overflow-x-auto">
            <h2 className="border-b border-slate-100 px-4 py-3 text-sm font-bold text-slate-700">Standing orders</h2>
            {statementQ.data.standingOrders.length === 0 ? (
              <EmptyState title="No standing orders" hint="Set one up for automatic monthly top-ups." />
            ) : (
              <table className="w-full">
                <thead>
                  <tr>
                    <th className="table-head">Source</th>
                    <th className="table-head text-right">Amount</th>
                    <th className="table-head">Frequency</th>
                    <th className="table-head">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {statementQ.data.standingOrders.map((s) => (
                    <tr key={s.id}>
                      <td className="table-cell">{s.source}</td>
                      <td className="table-cell text-right tabular-nums">{scr(s.amount)}</td>
                      <td className="table-cell">{s.frequency}</td>
                      <td className="table-cell">
                        <StatusBadge status={s.active ? 'ACTIVE' : 'CLOSED'} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      ) : null}

      {modal === 'deposit' ? <DepositModal nin={persona?.nin} onClose={() => setModal(null)} onDone={reload} /> : null}
      {modal === 'withdraw' ? (
        <WithdrawModal nin={persona?.nin} balance={statementQ.data?.balance ?? 0} onClose={() => setModal(null)} />
      ) : null}
      {modal === 'standing' ? (
        <StandingOrderModal nin={persona?.nin} onClose={() => setModal(null)} onDone={reload} />
      ) : null}
    </div>
  );
}

function DepositModal({ nin, onClose, onDone }: { nin?: string; onClose: () => void; onDone: () => void }) {
  const [amount, setAmount] = useState('');
  const [ref, setRef] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!nin) {
      setError('No NIN on file for this session.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/voluntary/deposits', {
        nin,
        amount: Number.parseFloat(amount),
        ref: ref || `SELF-${Date.now()}`,
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Deposit failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Make a voluntary deposit" onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <Field label="Amount (SCR)">
          <input className="field-input" type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} />
        </Field>
        <Field label="Payment reference (optional)">
          <input className="field-input" value={ref} onChange={(e) => setRef(e.target.value)} placeholder="e.g. bank transfer reference" />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || !amount}>
            {busy ? 'Depositing…' : 'Deposit'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function WithdrawModal({ nin, balance, onClose }: { nin?: string; balance: number; onClose: () => void }) {
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async () => {
    if (!nin) {
      setError('No NIN on file for this session.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/voluntary/accounts/${nin}/withdrawals`, { amount: Number.parseFloat(amount), reason });
      setDone(true);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Withdrawal request failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Request a withdrawal" onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        {done ? (
          <div className="rounded-lg bg-emerald-50 px-3.5 py-2.5 text-sm text-emerald-700">
            Withdrawal requested. An SPF finance officer will review and release payment.
          </div>
        ) : (
          <>
            <p className="text-xs text-slate-500">Available balance: {scr(balance)}</p>
            <Field label="Amount (SCR)">
              <input className="field-input" type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} />
            </Field>
            <Field label="Reason">
              <textarea className="field-input" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
            </Field>
          </>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            {done ? 'Close' : 'Cancel'}
          </button>
          {!done ? (
            <button className="btn-primary" onClick={submit} disabled={busy || !amount || reason.length < 3}>
              {busy ? 'Submitting…' : 'Request withdrawal'}
            </button>
          ) : null}
        </div>
      </div>
    </Modal>
  );
}

function StandingOrderModal({ nin, onClose, onDone }: { nin?: string; onClose: () => void; onDone: () => void }) {
  const [source, setSource] = useState('');
  const [amount, setAmount] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!nin) {
      setError('No NIN on file for this session.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/voluntary/accounts/${nin}/standing-orders`, {
        source,
        amount: Number.parseFloat(amount),
        frequency: 'MONTHLY',
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to set up standing order');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Set up a monthly standing order" onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <Field label="Source">
          <input
            className="field-input"
            value={source}
            onChange={(e) => setSource(e.target.value)}
            placeholder="e.g. employer payroll deduction, bank standing order"
          />
        </Field>
        <Field label="Monthly amount (SCR)">
          <input className="field-input" type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || !source || !amount}>
            {busy ? 'Saving…' : 'Set up standing order'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
